// react
import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';

// third-party
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';

import PageHeader from '../shared/PageHeader';
import EmptyState from '../shared/EmptyState';
import { url } from '../../services/utils';
import theme from '../../data/theme';
import { updateAction } from '../../store/auth/authActions';
import { getCartList, updateCartCache, clearCartCache } from '../../services/cartService';

export default function ShopPageCart() {
    const [cart, setCart] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingCO, setLoadingCO] = useState(false);

    const isInitialLoad = useRef(true);
    const isFetching = useRef(false);

    const { token, update: updateStatus } = useSelector((state) => state.auth);
    const history = useHistory();
    const dispatch = useDispatch();

    // Helper: update realtime header cart count immediately
    const updateRealtimeCount = (itemsList) => {
        const list = Array.isArray(itemsList) ? itemsList : [];
        const { total } = updateCartCache(list);
        window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: total }));
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: total, source: 'ShopPageCart' } }));
    };

    const getActiveToken = () => {
        if (token) return token;
        try {
            const stored = JSON.parse(localStorage.getItem('state') || '{}');
            const found = stored?.auth?.token || localStorage.getItem('token');
            if (found) {
                localStorage.setItem('token', found);
                return found;
            }
        } catch (e) {
            return localStorage.getItem('token');
        }
        return localStorage.getItem('token');
    };

    const fetchCart = async (force = false) => {
        if (isFetching.current) return;
        const activeToken = getActiveToken();
        if (!activeToken) {
            setLoading(false);
            return;
        }
        isFetching.current = true;
        try {
            const { items, total } = await getCartList(activeToken, force);

            setSelectedIds((prevSelected) => {
                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                    return items.map((i) => i.id);
                }
                // Preserve current user selections
                const validIds = new Set(items.map((i) => String(i.id)));
                const prevValid = prevSelected.filter((id) => validIds.has(String(id)));

                // Automatically select newly added items from search suggestions or other components
                const prevKnownIds = new Set(prevSelected.map((id) => String(id)));
                const newItems = items.filter((i) => !prevKnownIds.has(String(i.id)));
                const newIds = newItems.map((i) => i.id);

                return Array.from(new Set([...prevValid, ...newIds]));
            });

            setCart(items);
            updateRealtimeCount(items);
            setLoading(false);
        } catch (error) {
            // Cart is empty (API returns 400 'Keranjang Kosong')
            setCart([]);
            setSelectedIds([]);
            updateRealtimeCount([]);
            setLoading(false);
        } finally {
            isFetching.current = false;
        }
    };

    useEffect(() => {
        fetchCart();
    }, [token]);

    // Real-time synchronization when items are added from Search Suggestions, Header, or other pages
    useEffect(() => {
        const handleExternalCartUpdate = (e) => {
            if (e?.detail?.source === 'ShopPageCart') return;
            clearCartCache();
            fetchCart(true);
        };

        window.addEventListener('cart:item_added', handleExternalCartUpdate);
        window.addEventListener('cart:updated', handleExternalCartUpdate);

        return () => {
            window.removeEventListener('cart:item_added', handleExternalCartUpdate);
            window.removeEventListener('cart:updated', handleExternalCartUpdate);
        };
    }, []);

    const formatRupiah = (money) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(money || 0);
    };

    // Helper: Check if specific item is selected
    const isItemChecked = (id) => selectedIds.some((selectedId) => String(selectedId) === String(id));

    // Helper: Check if all items in cart are selected
    const isAllSelected = cart.length > 0 && cart.every((item) => isItemChecked(item.id));

    // Toggle Select All items
    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(cart.map((i) => i.id));
        }
    };

    // Toggle Single Item
    const toggleSelectItem = (id) => {
        setSelectedIds((prev) => {
            const idStr = String(id);
            const exists = prev.some((selectedId) => String(selectedId) === idStr);
            if (exists) {
                return prev.filter((selectedId) => String(selectedId) !== idStr);
            } else {
                return [...prev, id];
            }
        });
    };

    // Helper: Check if all items in a store/warehouse are selected
    const isStoreSelected = (storeItems) => {
        return storeItems.length > 0 && storeItems.every((item) => isItemChecked(item.id));
    };

    // Toggle Select All items in a store
    const toggleStoreSelect = (storeItems) => {
        const allChecked = isStoreSelected(storeItems);
        const storeItemIds = storeItems.map((i) => String(i.id));

        if (allChecked) {
            // Deselect only items belonging to this store
            setSelectedIds((prev) => prev.filter((id) => !storeItemIds.includes(String(id))));
        } else {
            // Select all items belonging to this store
            setSelectedIds((prev) => {
                const newSet = new Set(prev.map(String));
                storeItemIds.forEach((id) => newSet.add(id));
                return cart.filter((item) => newSet.has(String(item.id))).map((item) => item.id);
            });
        }
    };

    // Optimistic, instant Quantity Update (lightweight, realtime)
    const handleUpdateQty = (item, newQty) => {
        if (newQty < 1) return;

        // 1. Instantly update cart state in 0ms
        const updatedCart = cart.map((c) =>
            String(c.id) === String(item.id)
                ? { ...c, qty: newQty, sub_total: (parseFloat(c.price_6) || 0) * newQty }
                : c
        );
        setCart(updatedCart);

        // 2. Realtime header badge update
        updateRealtimeCount(updatedCart);

        // 3. Silent background API sync
        axios({
            method: 'POST',
            url: `${process.env.REACT_APP_HOST_API}/cart/update/${item.id}`,
            headers: {
                Authorization: `Bearer ${token}`,
            },
            data: { qty: newQty },
        }).catch((error) => {
            console.error('Error updating qty:', error);
            toast.error('Gagal memperbarui jumlah');
            fetchCart();
        });
    };

    // Optimistic, instant Remove Single Item
    const handleRemoveItem = async (itemId) => {
        // 1. Instantly update cart and selection
        const updatedCart = cart.filter((i) => String(i.id) !== String(itemId));
        setCart(updatedCart);
        setSelectedIds((prev) => prev.filter((id) => String(id) !== String(itemId)));

        // 2. Realtime header badge update
        updateRealtimeCount(updatedCart);

        // 3. Background API call
        try {
            await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/cart/delete/${itemId}`,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                data: { qty: 0 },
            });
            toast.success('Barang dihapus dari keranjang');
            updateRealtimeCount(updatedCart);
        } catch (error) {
            console.error('Error removing item:', error);
            toast.error('Gagal menghapus produk');
            clearCartCache();
            fetchCart(true);
        }
    };

    // Optimistic, instant Delete Selected Items
    const handleDeleteSelected = async () => {
        const selectedItems = cart.filter((item) => isItemChecked(item.id));
        if (selectedItems.length === 0) return;

        const idsToDelete = selectedItems.map((item) => item.id);

        // 1. Instantly update cart and selection
        const updatedCart = cart.filter((item) => !idsToDelete.some((delId) => String(delId) === String(item.id)));
        setCart(updatedCart);
        setSelectedIds([]);

        // 2. Realtime header badge update
        updateRealtimeCount(updatedCart);

        // 3. Background API calls
        try {
            for (const id of idsToDelete) {
                await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_HOST_API}/cart/delete/${id}`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    data: { qty: 0 },
                });
            }
            toast.success(`${idsToDelete.length} barang berhasil dihapus`);
            updateRealtimeCount(updatedCart);
        } catch (error) {
            console.error('Error batch deleting:', error);
            toast.error('Gagal menghapus beberapa barang');
            clearCartCache();
            fetchCart(true);
        }
    };

    // Checkout: Only selected items!
    const handleCheckout = async () => {
        const selectedItems = cart.filter((item) => isItemChecked(item.id));
        if (selectedItems.length === 0) {
            toast.warning('Pilih minimal 1 barang untuk dibeli');
            return;
        }

        const activeToken = getActiveToken();
        if (!activeToken) {
            toast.warning('Silakan login terlebih dahulu untuk checkout');
            history.push('/account/login');
            return;
        }

        let user_code = localStorage.getItem('user_code');
        if (!user_code) {
            try {
                const infoRes = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/auth/info`,
                    headers: {
                        Authorization: `Bearer ${activeToken}`,
                    },
                });
                if (infoRes.data?.response?.user_code) {
                    user_code = infoRes.data.response.user_code;
                    localStorage.setItem('user_code', user_code);
                }
            } catch (e) {
                console.error('Failed to get user_code:', e);
            }
        }

        const selectedCartIds = selectedItems.map((item) => item.id);
        const param = {
            user_code: user_code,
            list_cart: selectedCartIds,
        };

        try {
            setLoadingCO(true);
            const result = await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/so-commerce/do-checkout`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: param,
            });

            setLoadingCO(false);
            localStorage.setItem('CO-INFO', JSON.stringify(result.data.response));
            dispatch(updateAction(true));
            setTimeout(() => {
                dispatch(updateAction(false));
            }, 1000);
            toast.success('Melanjutkan ke pembayaran');
            history.push('/shop/checkout');
        } catch (error) {
            setLoadingCO(false);
            console.error('Checkout error:', error);
            toast.error(error.response?.data?.message || 'Gagal memproses checkout');
        }
    };

    // Calculate totals for selected items ONLY
    const selectedItems = cart.filter((item) => isItemChecked(item.id));
    const selectedTotalPrice = selectedItems.reduce((sum, item) => {
        const subTotal = parseFloat(item.sub_total);
        if (!isNaN(subTotal) && subTotal > 0) {
            return sum + subTotal;
        }
        const price = parseFloat(item.price_6) || 0;
        const qty = parseInt(item.qty, 10) || 1;
        return sum + (price * qty);
    }, 0);

    // Group items by store / warehouse
    const storeGroups = cart.reduce((groups, item) => {
        const storeName = item.warehouse_name || 'Hokky Bangunan Official';
        if (!groups[storeName]) {
            groups[storeName] = [];
        }
        groups[storeName].push(item);
        return groups;
    }, {});

    const breadcrumb = [
        { title: 'Home', url: '/' },
        { title: 'Keranjang', url: '/shop/cart' },
    ];

    return (
        <div className="hokky-cart-page-block">
            <Helmet>
                <title>{`Keranjang | ${theme.name}`}</title>
            </Helmet>

            <PageHeader breadcrumb={breadcrumb} />

            <div className="container hokky-cart-container">
                {loading ? (
                    <div className="hokky-cart-layout cart-skeleton-layout">
                        {/* Left Column: Items List Skeleton */}
                        <div className="hokky-cart-main-col">
                            {/* Card 1: Select All Placeholder */}
                            <div className="hokky-card hokky-cart-select-all cart-skeleton-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div className="skeleton-bone" style={{ width: 20, height: 20, borderRadius: 5 }} />
                                    <div className="skeleton-bone" style={{ width: 140, height: 16, borderRadius: 4 }} />
                                </div>
                            </div>

                            {/* Store Groups Placeholder */}
                            {[1, 2].map((storeIdx) => (
                                <div key={storeIdx} className="hokky-card hokky-store-card cart-skeleton-card">
                                    <div className="hokky-store-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div className="skeleton-bone" style={{ width: 20, height: 20, borderRadius: 5 }} />
                                        <div className="skeleton-bone" style={{ width: storeIdx === 1 ? 190 : 150, height: 18, borderRadius: 4 }} />
                                    </div>

                                    <div className="hokky-items-container">
                                        {[1, 2].slice(0, storeIdx === 1 ? 2 : 1).map((itemIdx) => (
                                            <div key={itemIdx} className="hokky-item-row">
                                                <div className="hokky-item-checkbox-col">
                                                    <div className="skeleton-bone" style={{ width: 20, height: 20, borderRadius: 5 }} />
                                                </div>

                                                <div className="hokky-item-thumb-box" style={{ background: '#f8fafc' }}>
                                                    <div className="skeleton-bone" style={{ width: '100%', height: '100%', borderRadius: 10 }} />
                                                </div>

                                                <div className="hokky-item-details-box">
                                                    <div className="hokky-item-top">
                                                        <div className="skeleton-bone" style={{ width: itemIdx === 1 ? '75%' : '60%', height: 16, borderRadius: 4, marginBottom: 6 }} />
                                                        <div className="skeleton-bone" style={{ width: '28%', height: 14, borderRadius: 4 }} />
                                                    </div>

                                                    <div className="hokky-item-bottom" style={{ marginTop: 12 }}>
                                                        <div className="hokky-price-area">
                                                            <div className="skeleton-bone" style={{ width: 110, height: 20, borderRadius: 4 }} />
                                                        </div>
                                                        <div className="hokky-actions-area">
                                                            <div className="skeleton-bone" style={{ width: 28, height: 28, borderRadius: 6 }} />
                                                            <div className="skeleton-bone" style={{ width: 92, height: 30, borderRadius: 20 }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Right Column: Sticky Summary Card Placeholder */}
                        <div className="hokky-cart-summary-col">
                            <div className="hokky-card hokky-summary-card cart-skeleton-card">
                                <div className="skeleton-bone" style={{ width: '50%', height: 20, borderRadius: 4 }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div className="skeleton-bone" style={{ width: 80, height: 14, borderRadius: 4 }} />
                                        <div className="skeleton-bone" style={{ width: 110, height: 16, borderRadius: 4 }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div className="skeleton-bone" style={{ width: 50, height: 16, borderRadius: 4 }} />
                                        <div className="skeleton-bone" style={{ width: 130, height: 22, borderRadius: 5 }} />
                                    </div>
                                </div>
                                <div className="skeleton-bone" style={{ width: '100%', height: 46, borderRadius: 12 }} />
                            </div>
                        </div>
                    </div>
                ) : cart.length > 0 ? (
                    <React.Fragment>
                        <div className="hokky-cart-layout">
                        {/* Left Column: Items List */}
                        <div className="hokky-cart-main-col">
                            {/* Card 1: Select All & Delete Action */}
                            <div className="hokky-card hokky-cart-select-all">
                                <label className="hokky-checkbox-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                        className="hokky-checkbox-input"
                                    />
                                    <span className="hokky-checkbox-box">
                                        <i className="fas fa-check" />
                                    </span>
                                    <span className="hokky-select-all-label">
                                        Pilih Semua <span className="count">({cart.length})</span>
                                    </span>
                                </label>

                                {selectedItems.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn-hokky-text-delete"
                                        onClick={handleDeleteSelected}
                                    >
                                        Hapus ({selectedItems.length})
                                    </button>
                                )}
                            </div>

                            {/* Store Groups */}
                            {Object.entries(storeGroups).map(([storeName, storeItems]) => {
                                const storeAllChecked = isStoreSelected(storeItems);
                                return (
                                    <div key={storeName} className="hokky-card hokky-store-card">
                                        <div className="hokky-store-header">
                                            <label className="hokky-checkbox-wrapper">
                                                <input
                                                    type="checkbox"
                                                    checked={storeAllChecked}
                                                    onChange={() => toggleStoreSelect(storeItems)}
                                                    className="hokky-checkbox-input"
                                                />
                                                <span className="hokky-checkbox-box">
                                                    <i className="fas fa-check" />
                                                </span>
                                                <span className="store-title">{storeName}</span>
                                            </label>
                                        </div>

                                        <div className="hokky-items-container">
                                            {storeItems.map((item) => {
                                                const isChecked = isItemChecked(item.id);
                                                return (
                                                    <div key={item.id} className="hokky-item-row">
                                                        <div className="hokky-item-checkbox-col">
                                                            <label className="hokky-checkbox-wrapper">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => toggleSelectItem(item.id)}
                                                                    className="hokky-checkbox-input"
                                                                />
                                                                <span className="hokky-checkbox-box">
                                                                    <i className="fas fa-check" />
                                                                </span>
                                                            </label>
                                                        </div>

                                                        <div className="hokky-item-thumb-box">
                                                            <Link to={url.product({ ...item, id: item.id_is || item.id })}>
                                                                <img
                                                                    src={item.image_url || '/images/products/placeholder.jpg'}
                                                                    alt={item.item_name}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = '/images/products/placeholder.jpg';
                                                                    }}
                                                                />
                                                            </Link>
                                                        </div>

                                                        <div className="hokky-item-details-box">
                                                            <div className="hokky-item-top">
                                                                <Link to={url.product({ ...item, id: item.id_is || item.id })} className="hokky-item-name">
                                                                    {item.item_name}
                                                                </Link>
                                                                <div className="hokky-item-badges">
                                                                    {item.satuan && (
                                                                        <span className="hokky-unit-badge">
                                                                            Satuan: {item.satuan}
                                                                        </span>
                                                                    )}
                                                                    <span
                                                                        className={`hokky-status-badge ${
                                                                            String(item.status || '').trim().toUpperCase() === 'INDEN' ||
                                                                            (item.stok != null && Number(item.stok) <= 0)
                                                                                ? 'hokky-status-badge--inden'
                                                                                : 'hokky-status-badge--ready'
                                                                        }`}
                                                                    >
                                                                        {String(item.status || '').trim().toUpperCase() === 'INDEN' ||
                                                                        (item.stok != null && Number(item.stok) <= 0)
                                                                            ? 'Inden'
                                                                            : 'Ready'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="hokky-item-bottom">
                                                                <div className="hokky-price-area">
                                                                    <span className="price-primary">
                                                                        <i className="fas fa-tag mr-1" />
                                                                        {formatRupiah(item.price_6)}
                                                                    </span>
                                                                </div>

                                                                <div className="hokky-actions-area">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-hokky-icon-action"
                                                                        onClick={() => handleRemoveItem(item.id)}
                                                                        title="Hapus barang"
                                                                    >
                                                                        <i className="far fa-trash-alt" />
                                                                    </button>

                                                                    <div className="hokky-stepper-pill">
                                                                        <button
                                                                            type="button"
                                                                            className="stepper-btn"
                                                                            onClick={() => handleUpdateQty(item, Math.max(1, (item.qty || 1) - 1))}
                                                                            disabled={item.qty <= 1}
                                                                            title="Kurangi"
                                                                        >
                                                                            <i className="fas fa-minus" />
                                                                        </button>
                                                                        <span className="stepper-count">{item.qty}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="stepper-btn"
                                                                            onClick={() => handleUpdateQty(item, (item.qty || 1) + 1)}
                                                                            title="Tambah"
                                                                        >
                                                                            <i className="fas fa-plus" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Right Column: Sticky Summary (Desktop & Tablet) */}
                        <div className="hokky-cart-summary-col d-none d-lg-block">
                            <div className="hokky-summary-card">
                                <h4 className="summary-title">Ringkasan Belanja</h4>

                                <div className="summary-total-row">
                                    <span className="label">Total</span>
                                    <span className="val">{formatRupiah(selectedTotalPrice)}</span>
                                </div>

                                <button
                                    type="button"
                                    className="btn-hokky-primary"
                                    onClick={handleCheckout}
                                    disabled={loadingCO || selectedItems.length === 0}
                                >
                                    {loadingCO ? (
                                        <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                        `Beli (${selectedItems.length})`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mode Beli Mengambang di Bawah untuk Mobile (Seperti Image 2, Elegan & Biru Hokky) */}
                    <div className="hokky-mobile-floating-buy d-lg-none">
                        <div className="hokky-mobile-floating-buy__content">
                            <label className="floating-buy__select-all" title="Pilih Semua">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={toggleSelectAll}
                                    className="floating-buy__checkbox-input"
                                />
                                <span className="floating-buy__checkbox-box">
                                    <i className="fas fa-check" />
                                </span>
                                <span className="floating-buy__select-label">Semua</span>
                            </label>

                            <div className="floating-buy__price-box">
                                <span className="floating-buy__price-label">Total</span>
                                <span className="floating-buy__price-val">{formatRupiah(selectedTotalPrice)}</span>
                            </div>

                            <button
                                type="button"
                                className="floating-buy__btn"
                                onClick={handleCheckout}
                                disabled={loadingCO || selectedItems.length === 0}
                            >
                                {loadingCO ? (
                                    <i className="fas fa-spinner fa-spin" />
                                ) : (
                                    `Beli (${selectedItems.length})`
                                )}
                            </button>
                        </div>
                    </div>
                    </React.Fragment>
                ) : (
                    /* Tokopedia Style Empty Cart (Clean & Modern) */
                    <EmptyState
                        icon="cart"
                        title="Keranjangmu masih kosong"
                        text="Yuk, isi dengan kebutuhan bangunanmu!"
                        actionLabel="Mulai Belanja"
                        actionTo="/"
                        actionIcon="shopping-bag"
                    />
                )}
            </div>
        </div>
    );
}
